import React, { useEffect, useMemo, useState } from "react";

const FEET_OPTIONS = Array.from({ length: 8 }, (_, index) => {
  const value = String(index + 1);
  return { value, label: `${value} ft` };
});

const INCH_OPTIONS = Array.from({ length: 12 }, (_, index) => {
  const value = String(index);
  return { value, label: `${value} in` };
});

export default function HeightInputField({
  valueCm,
  onChangeCm,
  disabled = false,
}) {
  const normalizedCm = normalizeHeightValue(valueCm);
  const [mode, setMode] = useState("cm");
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");

  useEffect(() => {
    if (!normalizedCm) {
      setFeet("");
      setInches("");
      return;
    }

    const next = cmToFeetInches(normalizedCm);
    setFeet(String(next.feet));
    setInches(String(next.inches));
  }, [normalizedCm]);

  const helperLabel = useMemo(() => {
    if (mode === "cm") {
      if (!normalizedCm) return "Enter height in centimeters";
      const imperial = cmToFeetInches(normalizedCm);
      return `${imperial.feet} ft ${imperial.inches} in`;
    }

    if (!normalizedCm) return "Height in cm will appear here";
    return `${normalizedCm} cm`;
  }, [mode, normalizedCm]);

  const handleCmChange = (event) => {
    const nextValue = String(event?.target?.value ?? event ?? "").replace(/[^\d]/g, "");
    onChangeCm?.(nextValue);
  };

  const handleFeetChange = (event) => {
    const nextFeet = String(event?.target?.value || "");
    setFeet(nextFeet);
    updateFromImperial(nextFeet, inches, onChangeCm);
  };

  const handleInchesChange = (event) => {
    const nextInches = String(event?.target?.value || "");
    setInches(nextInches);
    updateFromImperial(feet, nextInches, onChangeCm);
  };

  return (
    <div style={{ ...styles.wrap, ...(disabled ? styles.wrapDisabled : {}) }}>
      {mode === "cm" ? (
        <div style={styles.cmRow}>
          <input
            type="text"
            inputMode="numeric"
            style={{ ...styles.input, ...styles.flexField }}
            value={normalizedCm}
            onChange={handleCmChange}
            placeholder="180"
            disabled={disabled}
            aria-label="Height in centimeters"
          />

          <div style={styles.switchBar}>
            <button
              type="button"
              style={{ ...styles.switchBtn, ...(mode === "cm" ? styles.switchBtnActive : {}) }}
              onClick={() => setMode("cm")}
              disabled={disabled}
            >
              Cm
            </button>
            <button
              type="button"
              style={{ ...styles.switchBtn, ...(mode === "imperial" ? styles.switchBtnActive : {}) }}
              onClick={() => setMode("imperial")}
              disabled={disabled}
            >
              Ft / In
            </button>
          </div>
        </div>
      ) : (
        <div style={styles.imperialRow}>
          <select
            style={{ ...styles.select, ...styles.flexField }}
            value={feet}
            onChange={handleFeetChange}
            disabled={disabled}
            aria-label="Feet"
          >
            <option value="">Feet</option>
            {FEET_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <select
            style={{ ...styles.select, ...styles.flexField }}
            value={inches}
            onChange={handleInchesChange}
            disabled={disabled || !feet}
            aria-label="Inches"
          >
            <option value="">Inches</option>
            {INCH_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <div style={styles.switchBar}>
            <button
              type="button"
              style={{ ...styles.switchBtn, ...(mode === "cm" ? styles.switchBtnActive : {}) }}
              onClick={() => setMode("cm")}
              disabled={disabled}
            >
              Cm
            </button>
            <button
              type="button"
              style={{ ...styles.switchBtn, ...(mode === "imperial" ? styles.switchBtnActive : {}) }}
              onClick={() => setMode("imperial")}
              disabled={disabled}
            >
              Ft / In
            </button>
          </div>
        </div>
      )}

      <div style={styles.helperText}>{helperLabel}</div>
    </div>
  );
}

function normalizeHeightValue(value) {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return String(Math.round(parsed));
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function cmToFeetInches(cmValue) {
  const totalInches = Math.round(Number(cmValue) / 2.54);
  return {
    feet: Math.floor(totalInches / 12),
    inches: totalInches % 12,
  };
}

function updateFromImperial(feetValue, inchesValue, onChangeCm) {
  const feet = Number.parseInt(String(feetValue || "").trim(), 10);
  const inches = Number.parseInt(String(inchesValue || "").trim(), 10);

  if (!Number.isFinite(feet)) {
    onChangeCm?.("");
    return;
  }

  const safeFeet = clamp(feet, 1, 8);
  const safeInches = Number.isFinite(inches) ? clamp(inches, 0, 11) : 0;
  const cm = Math.round(((safeFeet * 12) + safeInches) * 2.54);
  onChangeCm?.(String(cm));
}

const styles = {
  wrap: {
    display: "grid",
    gap: 6,
  },
  wrapDisabled: {
    opacity: 0.86,
  },
  cmRow: {
    display: "flex",
    gap: 14,
    alignItems: "center",
  },
  imperialRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  flexField: {
    minWidth: 0,
    flex: 1,
  },
  switchBar: {
    display: "inline-flex",
    padding: 3,
    borderRadius: 999,
    background: "#fff2ef",
    border: "1px solid rgba(185,52,58,0.14)",
    gap: 3,
    flexShrink: 0,
    marginLeft: 2,
  },
  switchBtn: {
    minWidth: 72,
    minHeight: 36,
    borderRadius: 999,
    border: "none",
    background: "transparent",
    color: "#6b6f7d",
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: "0.04em",
    cursor: "pointer",
  },
  switchBtnActive: {
    background: "#D73C2E",
    color: "#ffffff",
    boxShadow: "0 8px 18px rgba(185,52,58,0.18)",
  },
  input: {
    width: "100%",
    minHeight: 42,
    borderRadius: 14,
    border: "1px solid rgba(185,52,58,0.22)",
    background: "#ffffff",
    padding: "0 14px",
    fontSize: 15,
    color: "#252935",
    outline: "none",
  },
  select: {
    width: "100%",
    minHeight: 42,
    borderRadius: 14,
    border: "1px solid rgba(185,52,58,0.22)",
    background: "#ffffff",
    padding: "0 14px",
    fontSize: 15,
    color: "#252935",
    outline: "none",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    backgroundImage:
      "linear-gradient(45deg, transparent 50%, #7a7f8f 50%), linear-gradient(135deg, #7a7f8f 50%, transparent 50%)",
    backgroundPosition: "calc(100% - 18px) calc(50% - 3px), calc(100% - 12px) calc(50% - 3px)",
    backgroundSize: "6px 6px, 6px 6px",
    backgroundRepeat: "no-repeat",
    paddingRight: 36,
  },
  helperText: {
    fontSize: 12,
    color: "#6b6f7d",
    paddingLeft: 2,
  },
};
